import { createSelector } from 'reselect'
import { equals } from 'ramda'
import { getInOr, has, filterNot } from 'utils/immutable'
import { List } from 'immutable'
import { createToJsSelector } from 'selectors/utils'
import { getIsSAndGEditable, getDashVehicleType } from 'selectors/dash'
import { VEHICLE_TYPE_M2, VEHICLE_TYPE_CHEC_PLUS } from 'utils/constants'
// no imports from selectors/dash, else circular deps issues

import {
  INTERNAL,
  EDIT_M2_S_AND_G,
  EDIT_PLUS_S_AND_G,
  EDIT_PRE_PROD_S_AND_G,
  EDIT_PLUS_PRE_PROD_S_AND_G,
  ADD_PLUS_VEHICLE_FAVORITES,
  ADD_M2_VEHICLE_FAVORITES,
  ADD_RELEASE_NOTES,
  REMOVE_42N_AS_BUILT_FEATURES,
  REMOVE_AS_BUILT_FEATURES,
  CREATE_APPROVED_DASH_LAYOUT,
  PLUS_CREATE_APPROVED_DASH_LAYOUT,
  ENGINEER,
  PLUS_ENGINEER,
  EDIT_OWN_USER_ROLES,
  EDIT_OWN_ROLE,
  CONFIGURE_SYSTEM_ALERT,
  VIEW_FAST_FEATURE_ERRORS,
} from 'constants/authorization'

import { ADMIN, PLUS_ADMIN, PLUS_VIEWER, VIEWER } from 'utils/authorization'

export const checkRights = (requiredRights, userRights) =>
  requiredRights.some((right) => userRights.has(right))

const isM2 = equals(VEHICLE_TYPE_M2)
const isChecPlus = equals(VEHICLE_TYPE_CHEC_PLUS)

export const userRightsSelector = getInOr(List(), [
  'user',
  'userData',
  'appRights',
])
export const userRolesSelector = getInOr(List(), [
  'user',
  'userData',
  'appRoles',
])

export const getIsUserInternal = createSelector(userRolesSelector, (roles) => {
  const isInternal = roles.includes(INTERNAL)
  return isInternal
})

export const getIsUserChecDev = createSelector(userRolesSelector, (roles) =>
  roles.includes('checDev')
)

export const isUserInternalAdmin = createSelector(
  userRolesSelector,
  getIsUserInternal,
  (roles, isInternal) =>
    (roles.includes(ADMIN) || roles.includes(PLUS_ADMIN)) && isInternal
)

export const getIsUserAdminOrEngineer = createSelector(
  userRolesSelector,
  (roles) =>
    roles.includes(ADMIN) ||
    roles.includes(PLUS_ADMIN) ||
    roles.includes(ENGINEER) ||
    roles.includes(PLUS_ENGINEER)
)

export const isUserViewer = createSelector(userRolesSelector, (roles) =>
  roles.includes(VIEWER)
)

export const isUserPlusViewer = createSelector(userRolesSelector, (roles) =>
  roles.includes(PLUS_VIEWER)
)

const rolesForDisplay = createSelector(
  userRolesSelector,
  filterNot(equals(INTERNAL))
)

export const getRolesForDisplay = createToJsSelector(rolesForDisplay)

export const hasRight = createSelector(
  (_, right) => right,
  userRightsSelector,
  has
)

export const hasRights = createSelector(
  userRightsSelector,
  (_, props) => props,
  (userRights, { rights }) => checkRights(rights, userRights)
)

export const makeHasRight = () => hasRight
export const makeHasRights = () => hasRights

const isPreProductionSelector = (state, props) =>
  state.getIn(['truck', 'vehicles', props.serialNumber, 'isPreProduction'])

export const getCanEditSwitchAndGauge = createSelector(
  userRightsSelector,
  getDashVehicleType,
  isPreProductionSelector,
  getIsSAndGEditable,
  (userRights, vehicleType, isPreProduction, isEditable) => {
    if (!isEditable) return false

    if (isM2(vehicleType)) {
      if (isPreProduction) return userRights.has(EDIT_PRE_PROD_S_AND_G)
      if (!isPreProduction) {
        return userRights.has(EDIT_M2_S_AND_G)
      }
      return false
    }

    if (isChecPlus(vehicleType)) {
      if (isPreProduction) {
        return userRights.has(EDIT_PLUS_PRE_PROD_S_AND_G)
      }
      if (!isPreProduction) {
        return userRights.has(EDIT_PLUS_S_AND_G)
      }
      return false
    }

    return false
  }
)

export const getCanAddVehicleFavorites = createSelector(
  (_state, { vehicleType }) => vehicleType,
  userRightsSelector,
  (vehicleType, userRights) =>
    (isM2(vehicleType) && has(ADD_M2_VEHICLE_FAVORITES, userRights)) ||
    (isChecPlus(vehicleType) && has(ADD_PLUS_VEHICLE_FAVORITES, userRights))
)

export const getCanAddReleaseNotes = createSelector(
  userRightsSelector,
  (userRights) => userRights.has(ADD_RELEASE_NOTES)
)

export const getCanRemoveAsBuiltFeature = createSelector(
  userRightsSelector,
  getDashVehicleType,
  (userRights, vehicleType) =>
    isM2(vehicleType)
      ? userRights.has(REMOVE_42N_AS_BUILT_FEATURES)
      : userRights.has(REMOVE_AS_BUILT_FEATURES)
)

/**
 * Internal E/A users can approve a dash layout.
 * The right CREATE_APPROVED_DASH_LAYOUT is only assigned to internal E/A roles in the DB,
 * so checking the right alone is sufficient.
 *
 * @return bool
 */
export const getCanApproveDashLayout = createSelector(
  userRightsSelector,
  (rights) =>
    rights.has(CREATE_APPROVED_DASH_LAYOUT) ||
    rights.has(PLUS_CREATE_APPROVED_DASH_LAYOUT)
)

// Only DTNA internal users may access Dash Builder.
// The backend injects 'internal' into appRoles for all NTLM-authenticated employees
// (AuthenticationMiddleware), so getIsUserInternal is the authoritative check.
export const getCanViewDashBuilder = createSelector(
  getIsUserInternal,
  (isInternal) => isInternal
)

// Check if user can edit their own roles.  Can be updated when rights/roles are
// updated to allow more granular control.
export const getCanEditOwnRoles = createSelector(
  userRightsSelector,
  userRolesSelector,
  (userRights, userRoles) =>
    userRights.has(EDIT_OWN_USER_ROLES) || userRoles.includes(EDIT_OWN_ROLE)
)

export const getCanConfigureSystemAlert = createSelector(
  userRightsSelector,
  (rights) => rights.has(CONFIGURE_SYSTEM_ALERT)
)

export const getCanViewFastFeatureErrors = createSelector(
  userRightsSelector,
  (rights) => rights.has(VIEW_FAST_FEATURE_ERRORS)
)

/**
 * Returns true once the user's appRights have been received from the API.
 *
 * The user reducer initialises without an appRights key. It is only written
 * by the GET_USER_INFO handler after the first API round-trip. Until that
 * point every rights selector returns false (empty List fallback), which would
 * incorrectly show NoAuth to authorised users navigating directly to a
 * rights-gated page.
 *
 * An explicitly received empty Set is a legitimate "no rights" state and must
 * still be treated as unauthorised — this selector returns true in that case
 * too, because the data has been loaded; it is just empty.
 */
export const getIsAuthLoaded = (state) =>
  state.getIn(['user', 'userData', 'appRights']) !== undefined
